import { Inject, Injectable } from '@angular/core';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { InjectConfig, PeConfigKey } from '@ddt-web/config-layer';
import { AppStoreConfig } from '@ddt-web/data-service';
import { PopupErrorObject } from '@ddt-web/error-handler';
import { StoreService } from '@ddt-web/store-service';
import { UrlService } from '@ddt-web/url';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as isEmpty from 'lodash/isEmpty';
import * as pick from 'lodash/pick';
import { combineLatest, of } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ApiParamsService } from './api-params.service';

@Injectable({
	providedIn: 'root'
})
@UntilDestroy()
export class PortfolioAnalysisValidatorService {

	@InjectConfig(PeConfigKey.MIN_HOLDINGS) private readonly minHoldings: number = 2;
	@InjectConfig(PeConfigKey.HOLDING_WEIGHT_LIMIT) private readonly maxHoldingPercentage: number = 75;

	// Also edit PortfolioEditorConstants.ERROR_TYPES. 
	// This is a duplicate due to circular dependency.
	private readonly portfolioEditorErrorTypes = new Map<string, { key: string; text: string }>([
		['min2Holdings', { key: 'MIN_2_HOLDINGS_ERROR', text: 'Please ensure that you have at least 2 holdings in your list.' }],
		['weightOverMax', { key: 'WEIGHT_OVER_MAX_ERROR', text: 'Please ensure that no holding has a weight greater than 75%. The following contain a holding that sums up to > 75%' }],
		['min3HoldingsCash', { key: 'MIN_3_HOLDINGS_CASH_ERROR', text: 'Please include an additional holding that is not CASH-USD.' }],
		['duplicateMorningstarFundId', { key: 'DUPLICATE_MSTAR_ID_ERROR', text: 'Please ensure that there are not multiple shareclasses of a single fund.' }]
	]);

	constructor(
		@Inject(APP_STORE_CONFIG_TOKEN) private storeConfig: AppStoreConfig,
		private storeService: StoreService,
		private urlService: UrlService,
		private apiParamsService: ApiParamsService
	) { }

	/**
	 * A portfolio can be created in Teamworld or Envestnet with having 2 holdings or duplicate shareclass. This is not allowed in AC. 
	 * This checks for those conditions when a portfolio is analysed. 
	 * 
	 * @param includeQueryParamCheck This is only for monitoring since the tool doesnt use ?portfolios
	 */
	public handleValidation(includeQueryParamCheck: boolean = true): void {
		combineLatest([
			includeQueryParamCheck ? this.urlService.queryParams$ : of({}),
			this.storeService.selectAll(this.storeConfig.associatedPortfolios),
			this.storeService.selectAll(this.storeConfig.positionDetails)
		]).pipe(
			untilDestroyed(this),
			filter(([queryParams, associatedPortfolios, positionDetails]) => {
				return (includeQueryParamCheck ? queryParams.portfolios : true) &&
					!isEmpty(associatedPortfolios.entities) && !associatedPortfolios.isLoading &&
					!isEmpty(positionDetails.entities) && !positionDetails.isLoading;
			})
		).subscribe(([queryParams, associatedPortfolios, positionDetails]: [any, any, any]) => {
			const portfolioIds = includeQueryParamCheck ? (Array.isArray(queryParams.portfolios) ? queryParams.portfolios : [queryParams.portfolios]) : positionDetails.ids;
			const flattenedAssociatedPortfolios = this.apiParamsService.flattenAssociatedPortfoliosData(portfolioIds, associatedPortfolios, true);
			for (const portfolio of Object.values(flattenedAssociatedPortfolios) as any[]) {
				// transient-book is for IQ, otherwise use portfolioId
				const positionDetail = positionDetails?.entities?.['transient-book'] ?? positionDetails?.entities?.[portfolio.id];
				const portfolioWithInfo = { ...pick(associatedPortfolios.entities[portfolio.id], ['description']), ...portfolio };
				this.checkErrors(portfolioWithInfo, positionDetail);
			}
		});
	}

	private checkErrors(portfolio: any, positionDetail: any): void {

		const positions = portfolio?.positions;

		// If positions contain index, ignore rules
		const containsIndex = positions?.some(position => position?.assetType === 'Index');

		if (!containsIndex) {
			// Cannot have a single security
			if (positions?.length < this.minHoldings) {
				this.throwError(portfolio, 'min2Holdings', this.portfolioEditorErrorTypes.get('min2Holdings'));
			}

			// Cannot have with a holding with weight >75%
			if (positions?.some(child => child.weight > this.maxHoldingPercentage)) {
				this.throwError(portfolio, 'weightOverMax', this.portfolioEditorErrorTypes.get('weightOverMax'));
			}

			// Cannot have 2 holdings where one of them is CASH-USD
			const isCash = child => child?.identifier?.includes('CASH') || child?.ticker?.includes('CASH') || child?.cusip?.includes('XUSD');
			if (positions?.length === 2 && positions?.some(isCash)) {
				this.throwError(portfolio, 'min3HoldingsCash', this.portfolioEditorErrorTypes.get('min3HoldingsCash'));
			}
		}

		// If a portfolio has < 4 holdings, user cannot have 2 or more shareclasses of the same fund (we use morningstarFundId to determine this relationship)
		if (positions?.length < 4 && positionDetail?.children.length) {
			const fundsByMstarFundId = {};
			for (const associatedPosition of positions) {
				const positionDetailChild = positionDetail?.children?.find(positionDetailChild => positionDetailChild.requestId === associatedPosition.identifier
					|| (positionDetailChild.globalId && associatedPosition.globalId && positionDetailChild.globalId === associatedPosition.globalId)
					|| (positionDetailChild.assetId && associatedPosition.assetId && positionDetailChild.assetId === associatedPosition.assetId)
				);
				const mstarFundId = positionDetailChild?.['morningstarFundId']
					?? positionDetailChild?.allData?.['morningstarFundId']
					?? positionDetailChild?.asset?.['morningstarFundId']
					?? positionDetailChild?.funds?.['morningstarFundId']?.stringValue;

				if (mstarFundId) {
					(fundsByMstarFundId[mstarFundId] ??= []).push(positionDetailChild);
				}
			}

			for (const funds of Object.values(fundsByMstarFundId) as any[]) {
				if (funds.length > 1) {
					this.throwError(portfolio, 'duplicateMorningstarFundId', this.portfolioEditorErrorTypes.get('duplicateMorningstarFundId'));
				}
			}
		}
	}

	private throwError(portfolio, errorCode, errorText): any {
		// Move this to a new thread so that the subscription above doesnt get killed 
		// since we're throwing an error here
		setTimeout(() => {
			throw new PopupErrorObject({ ...portfolio, errorCode, errorText });
		}, 100);
	}
}
